import { useMutation } from 'convex/react';
import { ConvexError } from 'convex/values';
import { api } from '@/convex/_generated/api';
import { CLIENT_REFRESH_THRESHOLD_MS } from '@/convex/media/url_freshness';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  cachedEntryIsServable,
  forgetStorageUrl,
  readStorageUrl,
  storageUrlCacheGeneration,
  writeStorageUrl,
  type CachedEntry,
} from './storage-url-cache';

// In-flight refresh guard, shared across ALL hook instances so two components
// rendering the same key (for the same actor) don't both call the refresh
// mutation. The loser does NOT silently give up: it reports `deferred`, and
// its caller re-arms a short timer that picks up whatever the winner wrote.
const refreshingKeys = new Set<string>();

// Delay before retrying a failed refresh (the mutation is idempotent).
const REFRESH_RETRY_DELAY_MS = 5_000;

// Floor for the proactive refresh timer. A server that somehow returns a URL
// already inside the refresh threshold would otherwise make the timer fire
// back-to-back; this turns that into a slow poll instead of a spin. It is also
// the delay a component waits when another component holds the in-flight slot
// for the same key.
const MIN_PROACTIVE_REFRESH_DELAY_MS = 5_000;

// Back-off used when a refresh completed but the expiry did NOT move forward.
// With the current thresholds the server always re-signs at the point the
// client asks, so this should not happen — but if it ever does (clock skew, a
// deployment mid-rollout with a narrower mint boundary), the timer must still
// re-arm rather than dying, and it must not turn into a 5s poll against a
// server that has nothing new to give.
const NON_ADVANCING_REFRESH_BACKOFF_MS = 30_000;

// Refresh errors that are access decisions, not transient failures: retrying
// them every 5s would only hammer the server with a refusal.
const ACCESS_DENIED_REFRESH_ERRORS = new Set([
  'STORAGE_ACCESS_DENIED',
  'AUTH_REQUIRED',
]);

/**
 * What one refresh attempt actually achieved. The caller needs the distinction:
 * `refreshed`/`observed` both carry a usable entry to render and to arm the next
 * timer against, `deferred`/`failed` carry none and mean "come back soon",
 * `denied` means the server refused this actor the key, and `stale` means the
 * memory was wiped (sign-out, account switch, access loss) while the attempt
 * was in flight, so its result belongs to nobody.
 */
type RefreshOutcome =
  | { kind: 'refreshed'; entry: CachedEntry }
  | { kind: 'observed'; entry: CachedEntry }
  | { kind: 'deferred' }
  | { kind: 'failed' }
  | { kind: 'denied' }
  | { kind: 'stale' };

type ResolverData = {
  url: string | null;
  urlExpiresAt: number | null;
  refreshRequired: boolean;
};

function isAccessDeniedRefreshError(error: unknown): boolean {
  return (
    error instanceof ConvexError &&
    typeof error.data === 'string' &&
    ACCESS_DENIED_REFRESH_ERRORS.has(error.data)
  );
}

/**
 * Refresh handshake for one private storage key: calls the idempotent
 * `refreshStorageUrl` mutation when the resolver asks for it, and proactively
 * before the rendered URL expires.
 *
 * Everything is tagged with the actor scope, the memory generation and the
 * key: a refresh that lands after a sign-out, account switch or workspace
 * switch (even one that comes back to the same actor) must never hand the
 * earlier session's URL to the next one.
 */
export function useStorageUrlRefresh({
  scope,
  storageKey,
  data,
  isAuthenticated,
  denied,
}: {
  scope: string | null;
  storageKey: string | null;
  data: ResolverData | undefined;
  isAuthenticated: boolean;
  /** The live resolver currently denies this key; stop refreshing it. */
  denied: boolean;
}): {
  refreshedEntry: CachedEntry | null;
  refreshDenied: boolean;
} {
  const generation = storageUrlCacheGeneration();
  const tag =
    storageKey && scope !== null
      ? `${generation}\u0000${scope}\u0000${storageKey}`
      : null;
  const refreshStorageUrl = useMutation(api.storage.resolver.refreshStorageUrl);
  // On failure, bump `retryTick` after a delay so the effect re-runs even though
  // its other dependencies (refreshRequired stays true) don't change.
  const [retryTick, setRetryTick] = useState(0);
  // The freshly-signed entry, mirrored into React state. The cache is a
  // module-level map, so writing to it is invisible to React on its own and a
  // long-mounted component would keep rendering the expired URL it already had.
  // Tagged with the actor + key it belongs to: `storageKey` and the actor can
  // change without remounting, and a refresh that lands after that must not
  // hand one key's (or one actor's) signed URL to another.
  const [refreshedFor, setRefreshedFor] = useState<{
    tag: string;
    entry: CachedEntry;
  } | null>(null);
  const refreshedEntry =
    refreshedFor && tag && refreshedFor.tag === tag ? refreshedFor.entry : null;
  // A refresh the server refused. It applies to the resolver result it was
  // raised against; a new live result (access granted again) supersedes it.
  const [deniedFor, setDeniedFor] = useState<{
    tag: string;
    data: ResolverData | undefined;
  } | null>(null);
  const refreshDenied =
    deniedFor !== null && deniedFor.tag === tag && deniedFor.data === data;
  // Adopting the SAME entry twice must not re-render. `observed` hands back the
  // exact object already in the module cache, so a bare `setRefreshedFor({...})`
  // would allocate a new wrapper every time and spin any effect that re-runs on
  // render identity.
  const adoptEntry = useCallback((entryTag: string, entry: CachedEntry) => {
    setRefreshedFor((prev) =>
      prev && prev.tag === entryTag && prev.entry === entry
        ? prev
        : { tag: entryTag, entry }
    );
  }, []);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // `useMutation` is not contractually identity-stable across renders, and this
  // hook's refresh loop must not be: an unstable `doRefresh` re-runs the effects
  // below on every render, and each re-run used to cancel the in-flight result
  // that was going to re-arm the timer — the loop simply stopped. Read the
  // mutation through a ref so `doRefresh` is created once.
  const refreshStorageUrlRef = useRef(refreshStorageUrl);
  useEffect(() => {
    refreshStorageUrlRef.current = refreshStorageUrl;
  }, [refreshStorageUrl]);
  // Cancellation is by UNMOUNT, not by effect re-run — see above.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  const doRefresh = useCallback(
    async (
      refreshScope: string | null,
      key: string,
      refreshTag: string
    ): Promise<RefreshOutcome> => {
      // Freshness is checked BEFORE the in-flight guard on purpose. A component
      // that arrives just after another one finished refreshing this key must
      // adopt the winner's entry — returning "nothing happened" would leave it
      // rendering its own expired URL and arming its next timer against a
      // dead expiry. Only the same actor's, recently access-checked entry
      // counts.
      const cached = readStorageUrl(refreshScope, key);
      if (cachedEntryIsServable(cached)) {
        return { kind: 'observed', entry: cached };
      }
      // Another component holds the slot for this key. Don't pile a second
      // mutation onto it; report `deferred` so the caller re-arms and picks up
      // the winner's result on the next tick.
      if (refreshingKeys.has(refreshTag)) return { kind: 'deferred' };
      refreshingKeys.add(refreshTag);
      const startedGeneration = storageUrlCacheGeneration();
      try {
        // Only the key goes to the server: the actor is whoever is
        // authenticated. The client's identity id is the Better Auth subject,
        // not the app users id, so it is never sent as an argument (#4747).
        const result = await refreshStorageUrlRef.current({ storageKey: key });
        // The memory was wiped while this was in flight: the result was
        // authorized for a session that has since ended.
        if (storageUrlCacheGeneration() !== startedGeneration) {
          return { kind: 'stale' };
        }
        const entry: CachedEntry = {
          url: result.url,
          urlExpiresAt: result.urlExpiresAt,
          validatedAt: Date.now(),
        };
        // Written under the scope the refresh was issued for: if the actor
        // changed meanwhile, the entry is unreachable for the new one.
        writeStorageUrl(refreshScope, key, entry);
        return { kind: 'refreshed', entry };
      } catch (error) {
        if (storageUrlCacheGeneration() !== startedGeneration) {
          return { kind: 'stale' };
        }
        if (isAccessDeniedRefreshError(error)) {
          forgetStorageUrl(key);
          return { kind: 'denied' };
        }
        // Leave cache as-is and schedule a retry for transient failures.
        if (retryTimerRef.current === null) {
          retryTimerRef.current = setTimeout(() => {
            retryTimerRef.current = null;
            setRetryTick((tick) => tick + 1);
          }, REFRESH_RETRY_DELAY_MS);
        }
        return { kind: 'failed' };
      } finally {
        refreshingKeys.delete(refreshTag);
      }
    },
    []
  );

  // Applies one outcome to this component; shared by both refresh triggers.
  const dataRef = useRef(data);
  useEffect(() => {
    dataRef.current = data;
  }, [data]);
  const applyOutcome = useCallback(
    (refreshTag: string, outcome: RefreshOutcome) => {
      // A completion from before the latest wipe, or for a tag this component
      // no longer renders, is discarded.
      if (!mountedRef.current || outcome.kind === 'stale') return;
      // `observed` counts: another instance already minted the URL this render
      // was waiting for.
      if (outcome.kind === 'refreshed' || outcome.kind === 'observed') {
        adoptEntry(refreshTag, outcome.entry);
      } else if (outcome.kind === 'denied') {
        setRefreshedFor(null);
        setDeniedFor({ tag: refreshTag, data: dataRef.current });
      }
    },
    [adoptEntry]
  );

  useEffect(() => {
    return () => {
      if (retryTimerRef.current !== null) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    };
  }, []);

  const active = !!storageKey && !!tag && isAuthenticated && !denied;
  useEffect(() => {
    if (!active || !storageKey || !tag || !data?.refreshRequired) return;
    void doRefresh(scope, storageKey, tag).then((outcome) => {
      applyOutcome(tag, outcome);
      // A wiped-mid-flight attempt is retried under the new generation's tag
      // (the re-render recomputes it) instead of leaving the consumer loading.
      if (outcome.kind === 'stale' && mountedRef.current) {
        setRetryTick((tick) => tick + 1);
      }
    });
  }, [
    active,
    scope,
    storageKey,
    tag,
    data?.refreshRequired,
    doRefresh,
    applyOutcome,
    retryTick,
  ]);

  // Proactive refresh for long-mounted components.
  //
  // A private URL expires on a wall clock, but the checks above only run when
  // something re-renders. A gallery, a viewer, or a chat transcript left open
  // past the TTL therefore kept rendering an expired URL until an unrelated
  // render happened to re-evaluate freshness — the image simply broke. This
  // schedules the refresh against the URL's own expiry instead, so a mounted
  // component always holds a live URL; unmounting clears the timer, and the
  // module-level `refreshingKeys` set still collapses duplicate refreshes
  // across every component showing the same key.
  //
  // Re-arming does NOT depend on the expiry moving. `schedule.tick` is bumped
  // every time an attempt settles, so the effect re-runs — and arms the next
  // timer — even when the attempt produced no new expiry at all (a deferred
  // attempt behind another component's in-flight refresh, a transient failure,
  // or a server that handed the same URL back). Keying the loop on
  // `currentExpiresAt` alone is exactly how a mounted component used to fall
  // off the timer and go on rendering a URL that then expired.
  const currentExpiresAt =
    active && storageKey && !refreshDenied
      ? (refreshedEntry?.urlExpiresAt ??
        readStorageUrl(scope, storageKey)?.urlExpiresAt ??
        data?.urlExpiresAt ??
        null)
      : null;
  const [proactiveSchedule, setProactiveSchedule] = useState({
    tick: 0,
    minDelayMs: MIN_PROACTIVE_REFRESH_DELAY_MS,
  });
  useEffect(() => {
    if (!storageKey || !tag || currentExpiresAt == null) return;
    const armedAgainst = currentExpiresAt;
    const delay = Math.max(
      proactiveSchedule.minDelayMs,
      armedAgainst - Date.now() - CLIENT_REFRESH_THRESHOLD_MS
    );
    const timer = setTimeout(() => {
      void doRefresh(scope, storageKey, tag).then((outcome) => {
        // Guarded by mount, not by this effect run: a re-render that re-runs
        // the effect must not discard the very result that re-arms the loop.
        applyOutcome(tag, outcome);
        // `stale` still re-arms: the re-render picks up the new generation's
        // tag, which drops the wiped session's refreshed entry.
        if (!mountedRef.current || outcome.kind === 'denied') return;
        const advanced =
          (outcome.kind === 'refreshed' || outcome.kind === 'observed') &&
          outcome.entry.urlExpiresAt != null &&
          outcome.entry.urlExpiresAt > armedAgainst;
        setProactiveSchedule((prev) => ({
          tick: prev.tick + 1,
          minDelayMs: advanced
            ? MIN_PROACTIVE_REFRESH_DELAY_MS
            : outcome.kind === 'refreshed'
              ? // The server had nothing newer to give. Re-arm, but slowly:
                // polling it at the 5s floor would be a stampede of its own.
                NON_ADVANCING_REFRESH_BACKOFF_MS
              : // Deferred behind another component, or a failed attempt whose
                // own retry timer is already running: come back promptly.
                MIN_PROACTIVE_REFRESH_DELAY_MS,
        }));
      });
    }, delay);
    return () => {
      clearTimeout(timer);
    };
  }, [
    scope,
    storageKey,
    tag,
    currentExpiresAt,
    doRefresh,
    applyOutcome,
    proactiveSchedule,
  ]);

  return { refreshedEntry, refreshDenied };
}
